import React, { useState } from "react";
import ReactDom from "react-dom";
import { useTranslation } from "react-i18next";
import classes from "./Modal.module.scss";
import Card from "../card/Card";
import Button from "../button/Button";

// Matches the exit animation durations in Modal.module.scss; the actual
// close callback is deferred until the animation finishes so the modal
// doesn't just disappear mid-transition.
const CLOSE_ANIMATION_MS = 200;

interface IBackdrop {
  onCancel: () => void;
  isClosing: boolean;
}
const Backdrop: React.FC<IBackdrop> = (props) => {
  return (
    <div
      className={`${classes.backdrop} ${props.isClosing ? classes.closing : ""}`}
      onClick={props.onCancel}
    ></div>
  );
};

interface IModal {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface IModalOverlay extends IModal {
  isClosing: boolean;
}

const ModalOverlay: React.FC<IModalOverlay> = (props) => {
  const { t } = useTranslation();

  return (
    <Card>
      <div className={`${classes.modal} ${props.isClosing ? classes.closing : ""}`}>
        <header className={classes.header}>
          <h3>{props.title}</h3>
        </header>
        <div className={classes.content}>
          <p>{props.message}</p>
        </div>
        <footer className={classes.actions}>
          <Button outline={true} onClick={props.onCancel} aria-label={t('cancel')}>
            {t("cancel")}
          </Button>
          <button className={classes.delete} onClick={props.onConfirm} aria-label={t('delete')}>
            {t("delete")}
          </button>
        </footer>
      </div>
    </Card>
  );
};

const Modal: React.FC<IModal> = (props) => {
  const [isClosing, setIsClosing] = useState(false);
  const backdropRoot = document.getElementById("backdrop-root") as HTMLElement;
  const modalOverlay = document.getElementById("overlay-root") as HTMLElement;

  const closeWith = (action: () => void) => {
    setIsClosing(true);
    setTimeout(action, CLOSE_ANIMATION_MS);
  };

  return (
    <>
      {ReactDom.createPortal(
        <Backdrop onCancel={() => closeWith(props.onCancel)} isClosing={isClosing} />,
        backdropRoot
      )}
      {ReactDom.createPortal(
        <ModalOverlay
          title={props.title}
          message={props.message}
          onConfirm={() => closeWith(props.onConfirm)}
          onCancel={() => closeWith(props.onCancel)}
          isClosing={isClosing}
        />,
        modalOverlay
      )}
    </>
  );
};

export default Modal;
